import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Faq } from '../services/faqService'
import { sanitizeFaqHtml } from '../utils/sanitize'

type Props = {
  items: Array<Pick<Faq, 'id' | 'title' | 'content'>>
  className?: string
}

export default function FaqAccordion({ items, className }: Props) {
  const [openId, setOpenId] = React.useState<string | null>(null)
  const toggle = (id: string) => setOpenId(prev => (prev === id ? null : id))
  return (
    <div className={cn('w-full', className)}>
      <ul className="divide-y divide-border rounded-xl border border-border overflow-hidden">
        {items.map((it) => (
          <li key={it.id}>
            <button
              type="button"
              aria-expanded={openId === it.id}
              aria-controls={`faq-panel-${it.id}`}
              onClick={() => toggle(it.id)}
              className={cn('w-full px-4 py-4 sm:px-6 sm:py-5 flex items-center justify-between text-left hover:bg-muted/40')}
            >
              <span className="text-sm sm:text-base font-medium text-foreground">{it.title}</span>
              <ChevronDown className={cn('h-4 w-4 text-foreground/70 transition-transform duration-200', openId === it.id && 'rotate-180')} />
            </button>
            <AnimatePresence initial={false}>
              {openId === it.id && (
                <motion.div
                  id={`faq-panel-${it.id}`}
                  role="region"
                  aria-labelledby={`faq-button-${it.id}`}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.22, ease: 'easeInOut' }}
                  className="overflow-hidden"
                >
                  <div
                    className="px-4 pb-4 sm:px-6 sm:pb-5 text-sm text-foreground/80 content-html"
                    dangerouslySetInnerHTML={{ __html: sanitizeFaqHtml(it.content || '') }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </li>
        ))}
      </ul>
    </div>
  )
}
